import { z } from 'zod';

export const questionTypeSchema = z.enum(['mcq', 'free_text', 'coding']);
export type QuestionType = z.infer<typeof questionTypeSchema>;

export const questionDifficultySchema = z.enum(['easy', 'medium', 'hard']);
export type QuestionDifficulty = z.infer<typeof questionDifficultySchema>;

/**
 * Public MCQ option — `correct` is intentionally omitted so clients cannot
 * read the answer key from the question bank API.
 */
export const mcqOptionPublicSchema = z.object({
  id: z.string().min(1),
  text: z.string().min(1),
});
export type McqOptionPublic = z.infer<typeof mcqOptionPublicSchema>;

/**
 * Client-facing question bank entry for assessment / practice UIs.
 * Spoiler fields (`exampleStrongAnswer`, `commonMistakes`, option.correct)
 * are not exposed here.
 */
export const questionSchema = z.object({
  id: z.string().min(1),

  skillId: z.string().min(1),
  rubricId: z.string().nullable(),
  type: questionTypeSchema,
  difficulty: questionDifficultySchema,
  prompt: z.string().min(1),
  expectedConcepts: z.array(z.string()),
  hints: z.array(z.string()),
  /** Present for `mcq`; null for free_text / coding. */
  options: z.array(mcqOptionPublicSchema).nullable(),
});
export type QuestionResponse = z.infer<typeof questionSchema>;

export const questionListResponseSchema = z.object({
  items: z.array(questionSchema),
});
export type QuestionListResponse = z.infer<typeof questionListResponseSchema>;

/** Query params for GET /v1/questions. */
export const listQuestionsQuerySchema = z.object({
  type: questionTypeSchema.optional(),
  skillId: z.string().min(1).optional(),
  difficulty: questionDifficultySchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListQuestionsQuery = z.infer<typeof listQuestionsQuerySchema>;
